import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Clock, ShieldCheck, Sparkles, Bell, Check, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import SEO from "@/components/SEO";
import WaitlistCTA from "@/components/home/WaitlistCTA";
import RateLockCard from "@/components/RateLockCard";

const API = process.env.REACT_APP_BACKEND_URL;

const CCY_SYMBOL = {
  USD: "$", GBP: "£", EUR: "€", CAD: "C$", AUD: "A$", CHF: "Fr",
  NGN: "₦", KES: "KSh", GHS: "GH₵", XOF: "CFA", ZAR: "R",
  UGX: "USh", MAD: "MAD", EGP: "E£", TZS: "TSh",
};

function fmt(n, decimals = 0) {
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default function CorridorDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [amount, setAmount] = useState(500);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/corridors/live/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => alive && setData(d))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (error) {
    return (
      <main className="pt-40 pb-32 bg-canvas min-h-[70vh]" data-testid="corridor-not-found">
        <div className="container-x">
          <div className="label-eyebrow">Corridor</div>
          <h1 className="mt-3 font-display font-medium text-forest text-4xl sm:text-5xl tracking-tightest">
            We don&apos;t route <span className="italic font-serif">{slug}</span> yet.
          </h1>
          <p className="mt-6 text-ink/70 max-w-lg">
            Vote for the corridors you need — the ones with the most requests get built first.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/#available-corridors" className="btn-accent" data-testid="corridor-back-home">
              See available corridors <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/waitlist" className="btn-ghost">
              Join the waitlist
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-40 pb-32 bg-canvas min-h-[70vh]">
        <div className="container-x animate-pulse space-y-4">
          <div className="h-4 w-32 bg-forest/10 rounded" />
          <div className="h-16 w-2/3 bg-forest/10 rounded" />
          <div className="h-4 w-1/3 bg-forest/10 rounded" />
        </div>
      </main>
    );
  }

  const fee = +(amount * 0.009).toFixed(2);
  const receive = Math.max(0, amount - fee) * data.rate;

  return (
    <main className="pt-32 md:pt-40 pb-24 bg-canvas" data-testid="corridor-detail-page">
      <SEO
        slug="countries"
        title={`Send ${data.from_ccy} to ${data.to_ccy} · ${data.from_country_name} → ${data.to_country_name} — Instantxfer CashApp`}
        description={`Live mid-market rate: 1 ${data.from_ccy} = ${fmt(data.rate, 2)} ${data.to_ccy}. Bank, mobile money and cash pickup in ${data.to_country_name}. 0.9% flat, arrives in ~2 min.`}
        image={`${API}/api/og/corridor/${data.slug}`}
      />

      <div className="container-x">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 label-eyebrow">
              <Sparkles className="w-4 h-4 text-forest" strokeWidth={1.75} />
              Live corridor
            </div>
            <h1 className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tightest break-words">
              <span className="mr-2 sm:mr-3">{data.flag_from}</span>
              {data.from_country_name}
              <br />
              <span className="italic font-serif text-forest/85">to</span>
              <span className="ml-2 sm:ml-3">{data.flag_to}</span> {data.to_country_name}.
            </h1>
            <p className="mt-6 md:mt-8 max-w-xl text-base sm:text-lg text-ink/70 leading-relaxed">
              Send {data.from_ccy} from {data.from_country_name} and land {data.to_ccy} in {data.to_country_name} at the
              real mid-market rate, with 0.9% flat and payout to any of the rails below.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg" data-testid="corridor-stats">
              <div>
                <div className="font-display text-3xl text-forest tracking-tightest">
                  {fmt(data.rate, data.rate < 10 ? 4 : 2)}
                </div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-ink/50 mt-1">
                  1 {data.from_ccy} = {data.to_ccy}
                </div>
              </div>
              <div>
                <div className="font-display text-3xl text-forest tracking-tightest">0.9%</div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-ink/50 mt-1">
                  Flat fee
                </div>
              </div>
              <div>
                <div className="font-display text-3xl text-forest tracking-tightest">~3 min</div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-ink/50 mt-1">
                  Median settle
                </div>
              </div>
            </div>
          </div>

          {/* Quote panel */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-forest text-canvas p-6 md:p-8" data-testid="corridor-quote">
              <div className="label-eyebrow !text-lime">Live quote</div>
              <label className="mt-4 block">
                <div className="text-[10px] tracking-[0.22em] uppercase text-canvas/60 font-semibold">You send</div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="font-display text-3xl md:text-4xl text-canvas tracking-tightest">
                    {CCY_SYMBOL[data.from_ccy] || data.from_ccy}
                  </span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(+e.target.value || 0)}
                    className="w-full bg-transparent font-display text-3xl md:text-4xl text-canvas tracking-tightest focus:outline-none"
                    aria-label="Amount"
                    data-testid="corridor-amount"
                  />
                </div>
              </label>
              <div className="mt-5 rounded-2xl bg-lime text-ink p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold">They receive</div>
                  <div className="mt-1 font-display text-2xl sm:text-3xl tracking-tightest break-words" data-testid="corridor-receive">
                    {CCY_SYMBOL[data.to_ccy] || data.to_ccy} {fmt(receive, 0)}
                  </div>
                  <div className="mt-1 text-[10px] text-ink/60">
                    Fee {CCY_SYMBOL[data.from_ccy] || data.from_ccy} {fmt(fee, 2)}
                  </div>
                </div>
                <Link
                  to={`/waitlist?send=${amount}&from=${data.from_ccy}&to=${data.to_ccy}&receive=${Math.round(receive)}&source=corridor-${data.slug}`}
                  className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-forest text-canvas px-4 py-2.5 rounded-full text-sm font-semibold"
                  data-testid="corridor-cta"
                >
                  Send in ~2 min <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-4 text-[11px] text-canvas/55 tracking-wide font-mono">
                Rate source · {data.source} · refreshed every 15 min
              </div>
            </div>
          </div>
        </div>

        {/* Payout rails */}
        <div className="mt-16 md:mt-24">
          <div className="label-eyebrow">Payout rails</div>
          <h2 className="mt-3 font-display font-medium text-forest text-3xl md:text-4xl tracking-tightest">
            How the money lands.
          </h2>
          <div
            className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="corridor-payout"
          >
            {data.payout_methods.map((m, i) => (
              <motion.div
                key={m}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="card-flat p-5 md:p-6"
              >
                <div className="w-10 h-10 rounded-full bg-forest text-canvas grid place-items-center">
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="mt-4 font-display text-forest text-lg tracking-tight">{m}</div>
                <div className="mt-1 text-xs text-ink/55">Median ~3 min · P95 &lt; 8 min</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Timing band */}
        <div className="mt-16 rounded-3xl bg-surface p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="label-eyebrow">Delivery</div>
            <h3 className="mt-3 font-display text-forest text-2xl md:text-3xl tracking-tight">
              {data.delivery}
            </h3>
            <p className="mt-3 text-ink/65 leading-relaxed">
              Direct rails on both sides — no correspondent-bank hop, no weekend blackout, no
              &ldquo;3–5 business days&rdquo; small print.
            </p>
          </div>
          <div className="md:col-span-5 flex items-center gap-4">
            <Clock className="w-8 h-8 text-forest" strokeWidth={1.5} />
            <div>
              <div className="font-display text-4xl text-forest tracking-tightest">~3 min</div>
              <div className="text-xs text-ink/55 tracking-[0.18em] uppercase mt-1">
                Median settlement
              </div>
            </div>
          </div>
        </div>
        {/* Rate lock — 24h signup incentive */}
        <RateLockCard
          slug={data.slug}
          fromCcy={data.from_ccy}
          toCcy={data.to_ccy}
          rate={data.rate}
        />

        {/* Rate alerts */}
        <RateAlertForm slug={data.slug} fromCcy={data.from_ccy} toCcy={data.to_ccy} />

        {/* Compare against another corridor */}
        <CorridorCompareBar current={data} />
      </div>

      <WaitlistCTA />
    </main>
  );
}


function RateAlertForm({ slug, fromCcy, toCcy }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | already | error
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setErrMsg("");
    try {
      const r = await fetch(`${API}/api/corridors/alerts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, slug }),
      });
      const d = await r.json();
      if (!r.ok) {
        setStatus("error");
        setErrMsg(d?.detail || "Please try again.");
        return;
      }
      setStatus(d.already_subscribed ? "already" : "ok");
    } catch (_) {
      setStatus("error");
      setErrMsg("Network hiccup. Try again in a second.");
    }
  };

  return (
    <div
      className="mt-16 rounded-3xl bg-forest text-canvas p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-center noise overflow-hidden relative"
      data-testid="rate-alert"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(400px 200px at 90% 10%, rgba(232,255,0,0.12), transparent 60%)",
        }}
      />
      <div className="md:col-span-7 relative">
        <div className="inline-flex items-center gap-2 label-eyebrow !text-lime">
          <Bell className="w-4 h-4" strokeWidth={2} />
          Rate alerts
        </div>
        <h3 className="mt-3 font-display font-medium text-canvas text-3xl md:text-4xl tracking-tightest leading-tight">
          Get pinged when {fromCcy} → {toCcy}{" "}
          <span className="italic font-serif text-lime">moves 1%</span> in your favour.
        </h3>
        <p className="mt-4 text-canvas/70 max-w-lg leading-relaxed">
          One email, only when the rate is meaningfully better. No spam, unsubscribe in one tap.
        </p>
      </div>

      <div className="md:col-span-5 relative">
        {status === "ok" || status === "already" ? (
          <div
            className="rounded-2xl bg-lime text-ink p-5 flex items-start gap-3"
            data-testid="rate-alert-success"
          >
            <div className="shrink-0 w-8 h-8 rounded-full bg-ink text-lime grid place-items-center">
              <Check className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-lg tracking-tight">
                {status === "already" ? "You're already on the list." : "You're in."}
              </div>
              <div className="mt-1 text-sm text-ink/70">
                We&apos;ll email <span className="font-mono">{email}</span> when {fromCcy} → {toCcy}{" "}
                moves at least 1%.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3" data-testid="rate-alert-form">
            <label className="block">
              <span className="text-[10px] tracking-[0.22em] uppercase text-canvas/60 font-semibold">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5 w-full rounded-xl bg-canvas/8 border border-canvas/20 text-canvas placeholder:text-canvas/40 px-4 py-3 focus:outline-none focus:border-lime/70 transition-colors"
                data-testid="rate-alert-email"
              />
            </label>
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 bg-lime text-ink px-5 py-3 rounded-full text-sm font-semibold hover:bg-lime/90 transition-colors disabled:opacity-60"
              data-testid="rate-alert-submit"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                  Sending…
                </>
              ) : (
                <>
                  Notify me when it moves <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </>
              )}
            </button>
            {status === "error" && (
              <div className="text-xs text-lime/80" data-testid="rate-alert-error">
                {errMsg}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}


// Corridor slugs we currently route live — mirrors ACTIVE list in AvailableCorridors.
const COMPARE_OPTIONS = [
  { slug: "usd-ngn", label: "USD → NGN · US to Nigeria" },
  { slug: "gbp-kes", label: "GBP → KES · UK to Kenya" },
  { slug: "eur-ghs", label: "EUR → GHS · EU to Ghana" },
  { slug: "usd-xof", label: "USD → XOF · US to Senegal" },
  { slug: "cad-zar", label: "CAD → ZAR · Canada to S. Africa" },
  { slug: "gbp-ngn", label: "GBP → NGN · UK to Nigeria" },
  { slug: "eur-mad", label: "EUR → MAD · EU to Morocco" },
  { slug: "usd-egp", label: "USD → EGP · US to Egypt" },
];

function Sparkline({ series, tone = "dark", toCcy }) {
  const [hover, setHover] = useState(null); // {i, x, y, v}
  if (!series || series.length < 2) return null;
  const w = 84, h = 22;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;
  const step = w / (series.length - 1);
  const points = series.map((v, i) => {
    const x = i * step;
    const y = h - ((v - min) / range) * h;
    return { i, x, y, v };
  });
  const pathPoints = points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
  const stroke = tone === "dark" ? "#E8FF00" : "#4A1D96";
  const fill = tone === "dark" ? "rgba(232,255,0,0.16)" : "rgba(74,29,150,0.10)";
  const dotColor = tone === "dark" ? "#E8FF00" : "#4A1D96";

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * w;
    let closest = points[0];
    let dist = Math.abs(x - closest.x);
    for (const p of points) {
      const d = Math.abs(x - p.x);
      if (d < dist) { closest = p; dist = d; }
    }
    setHover(closest);
  };

  const daysAgoLabel = (i) => {
    const n = series.length - 1 - i;
    if (n === 0) return "today";
    if (n === 1) return "1 day ago";
    return `${n} days ago`;
  };

  return (
    <div className="relative inline-block" onMouseLeave={() => setHover(null)}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width={w}
        height={h}
        className="opacity-90 cursor-crosshair"
        aria-label="7-day rate sparkline"
        data-testid="compare-sparkline"
        onMouseMove={onMove}
      >
        <polygon points={`0,${h} ${pathPoints} ${w},${h}`} fill={fill} />
        <polyline points={pathPoints} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {hover && (
          <>
            <line x1={hover.x} y1={0} x2={hover.x} y2={h} stroke={stroke} strokeWidth="0.7" strokeDasharray="1.5 1.5" opacity="0.5" />
            <circle cx={hover.x} cy={hover.y} r="2.4" fill={dotColor} stroke={tone === "dark" ? "#1A083E" : "#FAFAFA"} strokeWidth="1" />
          </>
        )}
      </svg>
      {hover && (
        <div
          className={
            "absolute -top-9 whitespace-nowrap px-2 py-1 rounded font-mono text-[10px] pointer-events-none " +
            (tone === "dark"
              ? "bg-lime text-ink shadow-[0_0_12px_rgba(232,255,0,0.4)]"
              : "bg-forest text-canvas")
          }
          style={{ left: `${(hover.x / w) * 100}%`, transform: "translateX(-50%)" }}
          data-testid="compare-sparkline-tooltip"
        >
          {hover.v.toFixed(hover.v < 10 ? 4 : 2)} {toCcy || ""} · {daysAgoLabel(hover.i)}
        </div>
      )}
    </div>
  );
}

function TrendArrow({ pct, tone = "dark" }) {
  if (pct === undefined || pct === null || Math.abs(pct) < 0.005) {
    return (
      <span
        className={
          "inline-flex items-center gap-1 text-[10px] font-mono tracking-wider " +
          (tone === "dark" ? "text-canvas/55" : "text-ink/45")
        }
        data-testid="compare-trend"
        data-direction="flat"
      >
        <Minus className="w-3 h-3" strokeWidth={2.5} />
        flat
      </span>
    );
  }
  const up = pct > 0;
  const cls = up
    ? tone === "dark"
      ? "text-lime"
      : "text-forest"
    : "text-canvas/55";
  return (
    <span
      className={"inline-flex items-center gap-1 text-[10px] font-mono tracking-wider " + cls}
      data-testid="compare-trend"
      data-direction={up ? "up" : "down"}
    >
      {up ? <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> : <TrendingDown className="w-3 h-3" strokeWidth={2.5} />}
      {up ? "+" : ""}
      {pct.toFixed(2)}%  · 24h
    </span>
  );
}

function BestBadge({ show, tone = "dark" }) {
  if (!show) return null;
  const cls =
    tone === "dark"
      ? "bg-lime text-ink"
      : "bg-forest text-lime border border-forest";
  return (
    <span
      className={
        "absolute -top-2 right-4 px-2 py-1 rounded-full text-[9px] tracking-[0.22em] uppercase font-bold shadow-[0_0_16px_rgba(232,255,0,0.4)] " +
        cls
      }
      data-testid="compare-best-badge"
    >
      Best today
    </span>
  );
}

function CorridorCompareBar({ current }) {
  const otherOptions = COMPARE_OPTIONS.filter((c) => c.slug !== current.slug);
  const [otherSlug, setOtherSlug] = useState(otherOptions[0]?.slug || "");
  const [otherData, setOtherData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!otherSlug) return;
    let alive = true;
    setLoading(true);
    fetch(`${API}/api/corridors/live/${otherSlug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => alive && setOtherData(d))
      .catch(() => alive && setOtherData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [otherSlug]);

  // Compare like-for-like: what would 1000 units of THIS corridor's from-ccy yield
  // vs. 1000 units of the other corridor's from-ccy (both after 0.9% fee).
  const amount = 1000;
  const thisReceive = Math.max(0, amount - amount * 0.009) * current.rate;
  const otherReceive = otherData
    ? Math.max(0, amount - amount * 0.009) * otherData.rate
    : 0;

  return (
    <div
      className="mt-16 rounded-3xl border border-forest/12 bg-surface p-6 md:p-10"
      data-testid="corridor-compare"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-7">
          <div className="label-eyebrow">Corridor compare</div>
          <h3 className="mt-3 font-display font-medium text-forest text-2xl sm:text-3xl md:text-4xl tracking-tightest leading-tight">
            See how <span className="italic font-serif">{current.from_ccy} → {current.to_ccy}</span> stacks up.
          </h3>
        </div>
        <div className="md:col-span-5">
          <label className="block">
            <span className="text-[10px] tracking-[0.22em] uppercase text-ink/50 font-semibold">
              Compare with
            </span>
            <select
              value={otherSlug}
              onChange={(e) => setOtherSlug(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-forest/15 bg-canvas px-3 py-3 text-sm text-forest font-mono focus:outline-none focus:border-forest/40 cursor-pointer"
              data-testid="compare-select"
            >
              {otherOptions.map((c) => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* THIS corridor */}
        <div className="relative rounded-2xl bg-forest text-canvas p-5" data-testid="compare-this-card">
          <BestBadge
            show={
              !!otherData &&
              current.to_ccy === otherData.to_ccy &&
              current.rate >= otherData.rate
            }
          />
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-lime">
            This corridor
          </div>
          <div className="mt-2 font-display text-xl tracking-tight">
            {current.flag_from} {current.from_ccy} → {current.flag_to} {current.to_ccy}
          </div>
          <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-canvas/55">
            1,000 {current.from_ccy} lands as
          </div>
          <div className="mt-1 font-display text-3xl md:text-4xl tracking-tightest text-lime break-words" data-testid="compare-this">
            {CCY_SYMBOL[current.to_ccy] || current.to_ccy} {fmt(thisReceive, 0)}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <TrendArrow pct={current.change_pct_24h} tone="dark" />
            <Sparkline series={current.series_7d} tone="dark" toCcy={current.to_ccy} />
          </div>
        </div>

        {/* OTHER corridor */}
        <div className="relative rounded-2xl border border-forest/15 bg-canvas p-5" data-testid="compare-other-card">
          <BestBadge
            tone="light"
            show={
              !!otherData &&
              current.to_ccy === otherData.to_ccy &&
              otherData.rate > current.rate
            }
          />
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-forest">
            Compared corridor
          </div>
          {loading ? (
            <div className="mt-3 flex items-center gap-2 text-ink/50 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> Loading…
            </div>
          ) : otherData ? (
            <>
              <div className="mt-2 font-display text-xl tracking-tight text-forest">
                {otherData.flag_from} {otherData.from_ccy} → {otherData.flag_to} {otherData.to_ccy}
              </div>
              <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-ink/50">
                1,000 {otherData.from_ccy} lands as
              </div>
              <div className="mt-1 font-display text-3xl md:text-4xl tracking-tightest text-forest break-words" data-testid="compare-other">
                {CCY_SYMBOL[otherData.to_ccy] || otherData.to_ccy} {fmt(otherReceive, 0)}
              </div>
              <div className="mt-2 flex items-center gap-3">
                <TrendArrow pct={otherData.change_pct_24h} tone="light" />
                <Sparkline series={otherData.series_7d} tone="light" toCcy={otherData.to_ccy} />
              </div>
            </>
          ) : (
            <div className="mt-3 text-ink/50 text-sm">Couldn&apos;t load — try another corridor.</div>
          )}
        </div>
      </div>

      {otherData && (
        <div
          className="mt-6 rounded-2xl bg-lime/15 border border-lime/40 p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          data-testid="compare-summary"
        >
          <div className="text-sm text-ink/75 leading-relaxed">
            <span className="font-mono text-xs text-forest tracking-wider">HEADS-UP</span>{" "}
            You can&apos;t compare receive amounts across different destination currencies directly —
            they&apos;re in different units. What you <em>can</em> see is how much value each corridor
            passes through: {current.from_ccy} → {current.to_ccy} keeps{" "}
            <b>{(current.rate * (1 - 0.009)).toFixed(4)}</b> {current.to_ccy} per {current.from_ccy},
            while {otherData.from_ccy} → {otherData.to_ccy} keeps{" "}
            <b>{(otherData.rate * (1 - 0.009)).toFixed(4)}</b> {otherData.to_ccy} per {otherData.from_ccy}.
          </div>
          <Link
            to={`/corridors/${otherData.slug}`}
            className="shrink-0 inline-flex items-center gap-1.5 bg-forest text-canvas px-4 py-2.5 rounded-full text-sm font-semibold"
            data-testid="compare-open-other"
          >
            Open corridor <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
